
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNotify } from '@/hooks/useNotify';
import { useImpersonationGuard } from '@/hooks/useImpersonationGuard';

export interface Company {
  id: string;
  name: string;
  email?: string;
  website?: string;
  address?: string;
  logo_url?: string;
  member_count: number;
  customer_count: number;
  admin_count: number;
  suspended?: boolean;
  status?: string;
}

export const useCompanyManagement = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const notify = useNotify();
  const { guardWrite } = useImpersonationGuard();

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .neq('name', 'System Default Company')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      const enhancedCompanies = await Promise.all(data.map(async (company) => {
        const [membersResult, customerCountResult, adminsResult] = await Promise.all([
          supabase
            .from('company_members')
            .select('user_id')
            .eq('company_id', company.id),
          supabase
            .from('customers')
            .select('*', { count: 'exact', head: true })
            .eq('created_by', company.id),
          supabase
            .from('company_members')
            .select('user_id')
            .eq('company_id', company.id)
            .eq('role', 'admin')
            .eq('status', 'active')
        ]);

        return {
          ...company,
          member_count: membersResult.data?.length || 0,
          customer_count: customerCountResult.count || 0,
          admin_count: adminsResult.data?.length || 0,
          suspended: company.status === 'suspended'
        };
      }));
      
      setCompanies(enhancedCompanies);
    } catch (error) {
      console.error('Error fetching companies:', error);
      notify.error(error);
    } finally {
      setLoading(false);
    }
  };

  const createCompany = async (companyData: Omit<Company, 'id' | 'member_count' | 'customer_count' | 'admin_count'>) => {
    if (!guardWrite('manage companies')) return false;
    try {
      const { data, error } = await supabase
        .from('companies')
        .insert(companyData)
        .select()
        .single();
        
      if (error) throw error;
      
      setCompanies([
        { 
          ...data, 
          member_count: 0, 
          customer_count: 0,
          admin_count: 0
        },
        ...companies
      ]);
      
      notify.success("Company created successfully");
      
      return true;
    } catch (error) {
      console.error('Error creating company:', error);
      notify.error(error);
      return false;
    }
  };

  const updateCompany = async (companyId: string, companyData: Partial<Company>) => {
    if (!guardWrite('manage companies')) return false;
    try {
      const { error } = await supabase
        .from('companies')
        .update(companyData)
        .eq('id', companyId);
        
      if (error) throw error;
      
      setCompanies(companies.map(company => 
        company.id === companyId ? { ...company, ...companyData } : company
      ));
      
      notify.success("Company updated successfully");
      
      return true;
    } catch (error) {
      console.error('Error updating company:', error);
      notify.error(error);
      return false;
    }
  };

  const deleteCompany = async (companyId: string) => {
    if (!guardWrite('manage companies')) return false;
    if (!confirm('Are you sure you want to delete this company? This action cannot be undone.')) {
      return false;
    }
    
    try {
      const { error } = await supabase
        .from('companies')
        .delete()
        .eq('id', companyId);
        
      if (error) throw error;
      
      setCompanies(companies.filter(company => company.id !== companyId));
      
      notify.success("Company deleted successfully");
      
      return true;
    } catch (error) {
      console.error('Error deleting company:', error);
      notify.error(error);
      return false;
    }
  };

  const suspendCompany = async (
    companyId: string,
    suspensionType: 'manual' | 'billing' = 'manual',
    suspensionReason?: string
  ) => {
    if (!guardWrite('suspend companies')) return false;
    try {
      const { data, error } = await supabase
        .rpc('suspend_company_members', {
          p_company_id: companyId,
          p_suspension_type: suspensionType,
          p_suspension_reason: suspensionReason || null
        } as any);
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error suspending company:', error);
      return false;
    }
  };

  const unsuspendCompany = async (companyId: string) => {
    if (!guardWrite('unsuspend companies')) return false;
    try {
      const { data, error } = await supabase
        .rpc('unsuspend_company_members', { p_company_id: companyId });
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error unsuspending company:', error);
      return false;
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  return {
    companies,
    loading,
    createCompany,
    updateCompany,
    deleteCompany,
    suspendCompany,
    unsuspendCompany,
    refetch: fetchCompanies
  };
};
